import JSZip from 'jszip'

/**
 * Converts a KMZ file to KML content as a string.
 * @param {File} kmzFile - The KMZ file to be converted.
 * @returns {Promise<string>} - A promise that resolves to the extracted KML content as a string.
 * @throws {Error} - Throws an error if the input is not a valid KMZ file, or if no KML file is found.
 */
export const convertKMZToKML = async (kmzFile: File): Promise<string> => {
  try {
    // Validate input: Ensure the file is a valid KMZ file
    if (!kmzFile || !(kmzFile instanceof File)) {
      throw new Error('The provided input is not a valid KMZ file.')
    }

    // Load the KMZ file as a ZIP archive using JSZip
    const zip = await JSZip.loadAsync(kmzFile)

    // Search for a file with the .kml extension inside the KMZ archive
    const kmlFileName = Object.keys(zip.files).find((fileName) =>
      fileName.toLowerCase().endsWith('.kml'),
    )

    // If no KML file is found, throw an error
    if (!kmlFileName) {
      throw new Error('No KML file was found inside the KMZ archive.')
    }

    // Extract the content of the KML file as a string
    const kmlContent = await zip.files[kmlFileName].async('string')

    return kmlContent
  } catch (error) {
    console.error('Error converting KMZ to KML:', error)
    throw new Error(`Conversion failed: ${error.message}`)
  }
}
